// "Under the lens · verdict" (DESIGN_SPEC §10.5, §11.4.2): the report's one hero number.
//   <VerdictLens ctx facts partial? />
//   score in the verdict colour + /100 · glyph + CAPS word + band (or the partial note) · meter · key/value
//   sheet (max severity, tags by severity, YARA, indicators with background hidden, injections and dumps,
//   run length and why it stopped) · Top signals (the four most severe tags). No verdict: a pending lens.
//   While the event stream has delivered nothing (figOrder streamPending) the event-derived rows say "not loaded".
import { Kv, Lens, LensScore, Meter, Sev, TopSignals, Verdict, type KvRow, type SevLevel } from "@/components/ui";
import { useThirdPartyRow } from "./panels/ThirdPartyFig";
import { clock, count, dur } from "@/lib/format";
import type { ReportCtx } from "@/lib/derive/types";
import type { Tag } from "@/types";
import { iocsOf } from "./derive/iocs";
import type { ReportFacts } from "./facts";
import { streamPending } from "./figOrder";
import { yaraRulesOfTags } from "./threats";

const SEV_LEVEL: Record<string, SevLevel> = { info: 1, low: 2, medium: 3, high: 4, critical: 5 };
const BAND: Record<string, string> = { benign: "band 0–24", suspicious: "band 25–59", malicious: "band 60–100" };

const NONE = <span className="text-ink-faint">none</span>;
const NOT_LOADED = <span className="text-ink-faint">not loaded</span>;

const clampSev = (n: number): SevLevel => Math.max(1, Math.min(5, Math.round(n))) as SevLevel;

/** The four most severe tags; the YARA rule tag is left out (the sheet above names the rule). */
function topSignals(tags: readonly Tag[]) {
  return tags
    .filter((t) => t.id !== "signature.yara-match")
    .sort((a, b) => b.severity - a.severity || b.hits - a.hits)
    .slice(0, 4)
    .map((t) => ({ sev: clampSev(t.severity), title: t.title, tech: t.mitre_ids?.[0] }));
}

/** "8 · 1 critical · 4 high · 1 medium" (medium and above named). */
function tagBreakdown(tags: readonly Tag[]) {
  const names = ["critical", "high", "medium"] as const;
  const by = names.map((n, i) => [n, tags.filter((t) => t.severity === 5 - i).length] as const).filter(([, c]) => c > 0);
  return (
    <>
      {count(tags.length)}
      {by.length > 0 && <small> · {by.map(([n, c]) => `${c} ${n}`).join(" · ")}</small>}
    </>
  );
}

/** The same count FIG Indicators shows (behavior-report.json, else what the stream extracted). */
function indicatorRow(ctx: ReportCtx) {
  const s = iocsOf(ctx);
  if (!s.shown && !s.background) return NONE;
  return (
    <>
      {count(s.shown)}
      {s.background > 0 && <small> · {count(s.background)} background hidden</small>}
    </>
  );
}

const STOP_WORDS: Record<string, string> = {
  "timeout reached": "stopped at timeout",
  "vm powered off": "the guest powered off",
  cancelled: "cancelled",
};

function runRow(ctx: ReportCtx, facts: ReportFacts) {
  const tr = ctx.stream.transitions;
  const t0 = ctx.stream.t0;
  if (t0 === null) return null;
  if (facts.state === "cancelled" || facts.state === "failed") {
    if (facts.atT === null) return null;
    const why = facts.reason ?? facts.state;
    return (
      <>
        {clock(facts.atT)}
        <small>
          {" "}
          · {facts.stopFromRecord ? `last event · ${why}` : why}
        </small>
      </>
    );
  }
  const collecting = tr.find((t) => t.state === "collecting");
  const end = collecting?.ts ?? tr.find((t) => t.state === "completed")?.ts;
  if (end === undefined || !Number.isFinite(end)) return null;
  const reason = collecting?.reason ? (STOP_WORDS[collecting.reason] ?? collecting.reason) : undefined;
  return (
    <>
      {dur(end - t0)}
      {reason && <small> · {reason}</small>}
    </>
  );
}

/** "PulsarRAT · 2 C2", from the config extractors (config.extracted events). */
function configRow(facts: ReportFacts): KvRow {
  const { families, c2, findings } = facts.config;
  const head = families[0] ?? `${count(findings)} ${findings === 1 ? "finding" : "findings"}`;
  const tail = [families.length > 1 ? `+${families.length - 1}` : "", c2.length ? `${count(c2.length)} C2` : ""].filter(Boolean).join(" · ");
  return ["Config", <>{head}{tail && <small> · {tail}</small>}</>];
}

export function VerdictLens({ ctx, facts, tags, partial }: { ctx: ReportCtx; facts: ReportFacts; tags: readonly Tag[]; partial?: boolean }) {
  const v = ctx.stream.verdict;
  const pending = streamPending(ctx.stream, ctx.job);
  // without the events, the rules come from the YARA tag's evidence (behavior-report.json)
  const yaraRules = pending ? yaraRulesOfTags(tags) : [...new Set(facts.yara.map((y) => y.rule))];
  const dumps = ctx.stream.snap.events.filter((e) => e.kind === "dump.taken").length || ctx.artifacts.filter((a) => a.kind === "process-dump").length;
  const inj = facts.injectedInto.length;
  const thirdParty = useThirdPartyRow(ctx.job);
  if (!v) {
    // nothing was scored: no hero number, only what was recorded
    return (
      <Lens label="Under the lens: verdict" eyebrow="Under the lens · verdict" className="rhead-lens">
        <p className="py-1">
          <Verdict v="pending" label="not scored" />
        </p>
        <Kv
          rows={[
            ["Events", pending ? NOT_LOADED : count(ctx.stream.snap.events.length)],
            ["Processes", pending ? NOT_LOADED : facts.processCount ? count(facts.processCount) : NONE],
            ["Injections", pending ? NOT_LOADED : inj ? count(inj) : NONE],
            ...(facts.config.findings ? [configRow(facts)] : []),
            ...(thirdParty ? [thirdParty] : []),
            ["Run", runRow(ctx, facts)],
          ]}
        />
      </Lens>
    );
  }
  const label = v.label;
  const maxSev = tags.length ? clampSev(Math.max(...tags.map((t) => t.severity))) : v.tagCount > 0 ? SEV_LEVEL[v.maxSeverity] : undefined;
  const why = partial
    ? facts.t0Source === "running"
      ? facts.atT !== null
        ? `partial · scored on the first ${clock(facts.atT)}`
        : "partial · scored before the run finished"
      : "partial · scored on boot telemetry only"
    : BAND[label];
  const yara = yaraRules.length ? (
    <>
      {yaraRules[0]}
      {yaraRules.length > 1 && <small> +{yaraRules.length - 1}</small>}
    </>
  ) : (
    NONE
  );
  const injections = pending ? (
    NOT_LOADED
  ) : inj || dumps ? (
    <>
      {count(inj)}
      {dumps > 0 && <small> · {dumps === 1 ? "1 process dump" : `${count(dumps)} process dumps`}</small>}
    </>
  ) : (
    NONE
  );
  const rows: KvRow[] = [
    ["Max severity", maxSev ? <Sev level={maxSev} /> : v.tagCount === 0 && !tags.length ? NONE : null],
    ["Behavior tags", tags.length ? tagBreakdown(tags) : NONE],
    ["YARA", yara],
    ...(facts.config.findings ? [configRow(facts)] : []),
    ...(thirdParty ? [thirdParty] : []),
    // the background split needs the events (CDN answers, answered addresses)
    ["Indicators", pending ? NOT_LOADED : indicatorRow(ctx)],
    ["Injections", injections],
    ["Run", runRow(ctx, facts)],
  ];
  return (
    <Lens label="Under the lens: verdict" eyebrow="Under the lens · verdict" className="rhead-lens">
      <LensScore value={v.score} of="/100" verdict={label} />
      <Verdict v={label} why={why} />
      <Meter value={v.score} verdict={label} />
      <Kv rows={rows} />
      <TopSignals items={topSignals(tags)} />
    </Lens>
  );
}
